/**
 * FirmaUyrukPage.jsx — Firma Uyrukları (backlog #313, 2026-10-11). Yalnızca personel.
 *
 * #312'den beri `firma.firma_nation` kural belirliyor: yabancı firma Vergi Levhası ve İSG belgesi
 * vermez, çalışanları kimlik / pasaportla kart alır (#314). Bugünkü değerler eski kayıt akışından geliyor
 * (yerelde 4.344 yabancı firma, aralarında organizatörler) — bu sayfa onları gözden geçirmek için:
 * uyruk, ülke ve organizatör süzgeci, arama; "şüpheli" satırlar = ülkesi Türkiye olan yabancı firma ya da
 * ülkesi başka olan yerli firma. Değiştirmek için satırdan firmanın profiline (Bilgiler sekmesindeki
 * "Uyruk ve Vergi Kimliği" kartı) gidilir.
 *
 * Backend: GET /firma/uyruk-listesi (FirmaController, doğrulanmış oturum + personel).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Card, CardBody, CardHeader, Col, Container, Input, Label, Pagination, PaginationItem,
  PaginationLink, Row, Spinner, Table,
} from "reactstrap";
import { Globe, AlertTriangle } from "lucide-react";
import { getUyrukListesi } from "../../../../services/portalService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../../i18n";

const SAYFA = 50;

const fmt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(currentLocaleTag());
};

export default function FirmaUyrukPage() {
  const { t: tx } = useTranslation("moduller");
  const [uyruk, setUyruk] = useState("2");
  const [ulke, setUlke] = useState("");
  const [organizator, setOrganizator] = useState(false);
  const [aramaGirdi, setAramaGirdi] = useState("");
  const [arama, setArama] = useState("");
  const [sayfa, setSayfa] = useState(0);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  useEffect(() => { document.title = i18n.t("moduller:firmaUyruk.firmaUyruklariFds"); }, []);

  // Arama yazarken her tuşta istek atmasın.
  useEffect(() => {
    const t = setTimeout(() => { setArama(aramaGirdi.trim()); setSayfa(0); }, 350);
    return () => clearTimeout(t);
  }, [aramaGirdi]);

  const params = useMemo(() => ({
    firmaNation: uyruk || undefined,
    turkiye: ulke === "" ? undefined : ulke === "1",
    organizator: organizator || undefined,
    search: arama || undefined,
    page: sayfa,
    size: SAYFA,
  }), [uyruk, ulke, organizator, arama, sayfa]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getUyrukListesi(params);
        if (!ignore) setVeri(out);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [params]);

  const toplam = veri?.total ?? 0;
  const sayfaSayisi = Math.max(1, Math.ceil(toplam / SAYFA));

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <Globe size={18} className="text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{tx("firmaUyruk.firmaUyruklari")}</h5>
            <Link to="/Management/Firmalar/Kartlar" className="btn btn-sm btn-outline-secondary">{tx("firmaUyruk.firmalar")}</Link>
          </CardHeader>
          <CardBody>
            <Alert color="light" className="small border">
              {tx("firmaUyruk.yabanciFirmaVergiLevhasiVe")}{" "}<strong>{tx("firmaUyruk.supheli")}</strong>{tx("firmaUyruk.ulkesiTurkiyeOlanYabanciFirma")}
            </Alert>
            <Row className="g-2 mb-3 align-items-end">
              <Col md={2}>
                <Label className="form-label small mb-1" for="fu-uyruk">{tx("firmaUyruk.uyruk")}</Label>
                <Input id="fu-uyruk" type="select" bsSize="sm" value={uyruk}
                       onChange={(e) => { setUyruk(e.target.value); setSayfa(0); }}>
                  <option value="">{tx("firmaUyruk.hepsi")}</option>
                  <option value="1">{tx("firmaUyruk.yerli")}</option>
                  <option value="2">{tx("firmaUyruk.yabanci")}</option>
                </Input>
              </Col>
              <Col md={2}>
                <Label className="form-label small mb-1" for="fu-ulke">{tx("firmaUyruk.ulke")}</Label>
                <Input id="fu-ulke" type="select" bsSize="sm" value={ulke}
                       onChange={(e) => { setUlke(e.target.value); setSayfa(0); }}>
                  <option value="">{tx("firmaUyruk.hepsi")}</option>
                  <option value="1">{tx("firmaUyruk.turkiye")}</option>
                  <option value="0">{tx("firmaUyruk.turkiyeDisiBos")}</option>
                </Input>
              </Col>
              <Col md={2}>
                <div className="form-check mb-1">
                  <Input id="fu-org" type="checkbox" className="form-check-input" checked={organizator}
                         onChange={(e) => { setOrganizator(e.target.checked); setSayfa(0); }} />
                  <Label for="fu-org" className="form-check-label small">{tx("firmaUyruk.yalnizOrganizatorler")}</Label>
                </div>
              </Col>
              <Col md={4}>
                <Label className="form-label small mb-1" for="fu-ara">{tx("common:ara")}</Label>
                <Input id="fu-ara" bsSize="sm" placeholder={tx("firmaUyruk.firmaAdiKisaAdVergi")} value={aramaGirdi}
                       onChange={(e) => setAramaGirdi(e.target.value)} />
              </Col>
              <Col md={2} className="text-end small text-muted">
                {yukleniyor ? <Spinner size="sm" /> : tx("firmaUyruk.firma", { v: toplam.toLocaleString(currentLocaleTag()) })}
              </Col>
            </Row>
            {hata && <Alert color="danger" className="small">{hata}</Alert>}
            <div className="table-responsive">
              <Table size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{tx("firmaUyruk.firma2")}</th><th>{tx("firmaUyruk.uyruk")}</th><th>{tx("firmaUyruk.ulke")}</th><th>{tx("firmaUyruk.vergiKimligi")}</th><th>{tx("firmaUyruk.organizator")}</th><th>{tx("firmaUyruk.sonIsaretleme")}</th>
                  </tr>
                </thead>
                <tbody>
                  {veri && veri.items.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted py-3">{tx("firmaUyruk.kayitYok")}</td></tr>
                  )}
                  {(veri?.items ?? []).map((f) => (
                    <tr key={f.firmaId} className={f.supheli ? "table-warning" : undefined}>
                      <td>
                        <Link to={`/Management/Firmalar/Profil/${f.firmaId}`} className="fw-semibold">{f.firmaName}</Link>
                        <div className="text-muted small font-monospace">#{f.firmaId}{f.firmaShortname ? ` · ${f.firmaShortname}` : ""}</div>
                      </td>
                      <td>
                        {f.yabanci ? <Badge color="info">{tx("firmaUyruk.yabanci")}</Badge> : <Badge color="secondary">{tx("firmaUyruk.yerli")}</Badge>}
                        {f.supheli && <AlertTriangle size={14} className="text-warning ms-1" title={tx("firmaUyruk.supheli")} />}
                      </td>
                      <td className="small">{f.countryName || <span className="text-muted">—</span>}</td>
                      <td className="small font-monospace">
                        {f.taxNumber || <span className="text-muted">—</span>}
                        {f.taxNumberLabel && <span className="text-muted"> ({f.taxNumberLabel})</span>}
                      </td>
                      <td className="small">{Number(f.organizatorFuarSayisi) > 0 ? tx("firmaUyruk.fuar", { organizatorFuarSayisi: f.organizatorFuarSayisi }) : "—"}</td>
                      <td className="small text-muted">
                        {fmt(f.nationTarihi) || "—"}
                        {f.nationNotu && <div className="fst-italic">{f.nationNotu}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {toplam > SAYFA && (
              <div className="d-flex align-items-center justify-content-between pt-2">
                <span className="text-muted small">{tx("firmaUyruk.sayfa", { v: sayfa + 1 })}{" "}{sayfaSayisi}</span>
                <Pagination size="sm" className="mb-0">
                  <PaginationItem disabled={sayfa === 0}>
                    <PaginationLink previous onClick={() => setSayfa((p) => Math.max(0, p - 1))} />
                  </PaginationItem>
                  <PaginationItem disabled={sayfa + 1 >= sayfaSayisi}>
                    <PaginationLink next onClick={() => setSayfa((p) => Math.min(sayfaSayisi - 1, p + 1))} />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
